import { Box, Stack, ThemeProvider, Typography } from '@mui/material'
import { FC, useMemo, useState } from 'react'
import { RescheduleStep, useRescheduleOrderModule } from 'components/common/RescheduleOrder'
import { Trans, useTranslation } from 'react-i18next'

import { MUIButton } from 'components/common/MUIButton'
import { MUICheckbox } from 'components/common/MUICheckBox'
import Modal from 'components/common/Modals/Modal/Modal'
import { QueryStatus } from 'components/common/QueryStatus'
import { RescheduleOrder } from 'components/common/RescheduleOrder/RescheduleOrder.module'
import { redesignMUITheme } from 'constants/styling/redesignTheme'
import { supportEmailHref } from 'constants/contacts'
import { useSnackbar } from 'components/contexts/SnackbarService.context'

interface Props {
  isOpen: boolean
  onClose: () => void
}

/**
 * Standalone modal containing Reschedule flow
 * @example <RescheduleOrderModal isOpen={isOpen} onClose={() => setIsOpen(false)} />
 */
export const RescheduleOrderModal: FC<Props> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation(['client_reschedule_modal', 'common', 'reschedule_order'])
  const { spawnSuccessToast } = useSnackbar()
  const { isValid, setStep, reset, step, reschedule, rescheduleMutation, isPenaltyRequired } = useRescheduleOrderModule()

  const [penaltyAccepted, setPenaltyAccepted] = useState(false)
  const isModalValid = useMemo(() => isValid && (!isPenaltyRequired || penaltyAccepted), [isPenaltyRequired, isValid, penaltyAccepted])

  return (
    <ThemeProvider theme={redesignMUITheme}>
      <Modal
        title={t('title')}
        isOpen={isOpen}
        onClose={() => onClose()}
        afterClosed={() => {
          reset()
          rescheduleMutation.reset()
          setPenaltyAccepted(false)
        }}
        footerContent={(
          <Stack gap={1} direction="row" justifyContent="flex-end" alignItems="center" maxWidth="600px">

            <Box flex="1 1 auto">

              {!isPenaltyRequired &&
                <Typography variant="text-xs">
                  <Trans t={t} i18nKey="conditions">
                    <a href={supportEmailHref()}>&nbsp;</a>
                  </Trans>
                </Typography>
              }

              {isPenaltyRequired &&
                <MUICheckbox
                  checked={penaltyAccepted}
                  onClick={() => setPenaltyAccepted((prev) => !prev)}
                  label={(
                    <Typography variant="text-xs">
                      <Trans t={t} i18nKey="conditions_penalty">
                        <a href={supportEmailHref()}>&nbsp;</a>
                      </Trans>
                    </Typography>
                  )}
                />
              }

            </Box>

            {step === RescheduleStep.FORM &&
              <>
                <MUIButton
                  sx={{ flex: '0 0 auto' }}
                  onClick={() => onClose()}
                  type="secondaryBorder"
                >
                  {t('common:Cancel')}
                </MUIButton>

                <MUIButton
                  sx={{ flex: '0 0 auto' }}
                  disabled={!isModalValid}
                  onClick={() => setStep(RescheduleStep.CONFIRM)}
                >
                  {t('reschedule')}
                </MUIButton>

              </>
            }

            {step === RescheduleStep.CONFIRM &&
              <>

                <MUIButton
                  sx={{ flex: '0 0 auto' }}
                  onClick={() => setStep(RescheduleStep.FORM)}
                  type="secondaryBorder"
                >
                  {t('common:Cancel')}
                </MUIButton>

                <MUIButton
                  sx={{ flex: '0 0 auto' }}
                  disabled={!isModalValid}
                  onClick={() => reschedule(() => {
                    spawnSuccessToast(t('reschedule_order:success_toast'))
                    onClose()
                  })}
                  isLoading={rescheduleMutation.isPending}
                >
                  {t('reschedule')}
                </MUIButton>

              </>
            }

          </Stack>
        )}
      >
        <Box width="90vw" maxWidth="600px">
          <RescheduleOrder />
          <QueryStatus
            query={rescheduleMutation}
            showStates={['error']}
            spaceTopRem={2}
            spaceBottomRem={2}
          />
        </Box>
      </Modal>
    </ThemeProvider>
  )
}
